import { Plan, PlanFeature } from '../dataModels/models';

export type SubscriptionPlanId = 'plan_basic' | 'plan_standard' | 'plan_premium';

export type SubscriptionPlanDefinition = {
  id: SubscriptionPlanId;
  name: string;
  shortName: string;
  price: string;
  subtitle: string;
  included: string[];
  excluded: string[];
  isComingSoon?: boolean;
};

export const SUBSCRIPTION_PLANS: SubscriptionPlanDefinition[] = [
  {
    id: 'plan_basic',
    name: 'Basic Plan',
    shortName: 'Basic',
    price: '9.99',
    subtitle: 'Best for getting started',
    included: [
      'Branded booking page',
      'Appointment scheduling & calendar',
      'Email confirmations & reminders',
      'Service & availability management',
      'Custom logo, colors & fonts',
      'Client cancel & reschedule self-service',
    ],
    excluded: [
      'Client messaging',
      'Branded booking URL',
      'SMS reminders',
      'Appointment deposits',
      'Advanced analytics',
      'Mobile push notifications',
    ],
  },
  {
    id: 'plan_standard',
    name: 'Standard Plan',
    shortName: 'Standard',
    price: '15.99',
    subtitle: 'Deposits, no-show protection, and upgraded branding',
    included: [
      'Everything in Basic',
      'Appointment deposits at booking',
      'No-show protection',
      'Provider-controlled deposit refunds',
      'Premium font & color themes',
      'Unlimited bookings',
    ],
    excluded: [
      'Email lists & marketing campaigns',
      'Branded booking URL',
      'Advanced analytics',
      'Mobile push notifications',
    ],
    isComingSoon: false,
  },
  {
    id: 'plan_premium',
    name: 'Premium Plan',
    shortName: 'Premium',
    price: '29.99',
    subtitle: 'For growing businesses managing multiple providers and locations',
    included: [
      'Everything in Standard',
      'Email lists & marketing campaigns',
      'Branded booking URL',
      'Multi-provider & staff management',
      'Advanced analytics',
      'Mobile push notifications',
    ],
    excluded: [],
  },
];

export function isPlanAvailableForLaunch(planId: SubscriptionPlanId): boolean {
  return planId === 'plan_basic' || planId === 'plan_standard';
}

export function normalizeSubscriptionPlanId(planId: string | null | undefined): SubscriptionPlanId | null {
  const normalized = planId?.trim().toLowerCase();

  switch (normalized) {
    case 'free':
    case 'basic':
    case 'plan_basic':
      return 'plan_basic';
    case 'standard':
    case 'plan_standard':
      return 'plan_standard';
    case 'premium':
    case 'plan_premium':
      return 'plan_premium';
    default:
      return null;
  }
}

export function resolveCurrentSubscriptionPlanId(plan: Pick<Plan, 'planId' | 'features'> | null | undefined): SubscriptionPlanId {
  const normalizedPlanId = normalizeSubscriptionPlanId(plan?.planId);

  if (normalizedPlanId) {
    return normalizedPlanId;
  }

  const features = new Set(plan?.features ?? []);
  if (
    features.has(PlanFeature.MARKETING_CAMPAIGNS)
    || features.has(PlanFeature.ADVANCED_ANALYTICS)
    || features.has(PlanFeature.MULTI_STAFF)
  ) {
    return 'plan_premium';
  }

  if (features.has(PlanFeature.SMS_NOTIFICATIONS)) {
    return 'plan_standard';
  }

  if (features.has(PlanFeature.EMAIL_NOTIFICATIONS)) {
    return 'plan_basic';
  }

  return 'plan_basic';
}

export function getPlanDisplayName(planId: string | null | undefined): string {
  switch (normalizeSubscriptionPlanId(planId)) {
    case 'plan_basic':
      return 'Basic';
    case 'plan_standard':
      return 'Standard';
    case 'plan_premium':
      return 'Premium';
    default:
      return 'Basic';
  }
}
